import { computed, ref, type ComputedRef } from 'vue'
import { ElMessage } from 'element-plus'
import { fetchDataOrderAnalysis, fetchDataOverview, fetchDataProductAnalysis, fetchStoreRank, type DataOrderAnalysis, type DataOverview, type DataProductAnalysis, type StoreRankItem } from '@/api/data'

type FormatAmount = (amountInFen: number) => string

export function useDataCenter(options: {
  isDataOverviewCenter: ComputedRef<boolean>
  isDataOrdersCenter: ComputedRef<boolean>
  isDataProductsCenter: ComputedRef<boolean>
  isStoreRankCenter: ComputedRef<boolean>
  formatAmount: FormatAmount
}) {
  const loadingDataOverview = ref(false)
  const loadingDataOrders = ref(false)
  const loadingDataProducts = ref(false)
  const loadingStoreRank = ref(false)
  const dataOverviewData = ref<DataOverview>()
  const dataOrderAnalysis = ref<DataOrderAnalysis>()
  const dataProductAnalysis = ref<DataProductAnalysis>()
  const storeRankRows = ref<StoreRankItem[]>([])

  const dataOverviewStats = computed(() => {
    const overview = dataOverviewData.value
    return [
      { label: '今日营业额', value: options.formatAmount(overview?.revenueToday ?? 0), sub: `昨日 ${options.formatAmount(overview?.revenueYesterday ?? 0)}`, tone: 'success' as const },
      { label: '今日支付订单', value: String(overview?.orderCountToday ?? 0), sub: `昨日 ${overview?.orderCountYesterday ?? 0} 单`, tone: 'brand' as const },
      { label: '今日退款单', value: String(overview?.refundCountToday ?? 0), sub: `昨日 ${overview?.refundCountYesterday ?? 0} 单`, tone: 'error' as const },
      { label: '统计日期', value: overview?.date || '--', sub: '后端实时聚合', tone: 'brand' as const },
    ]
  })

  const dataOrderStats = computed(() => {
    const analysis = dataOrderAnalysis.value
    return [
      { label: '支付订单', value: String(analysis?.paidCount ?? 0), sub: '累计支付订单', tone: 'success' as const },
      { label: '取消订单', value: String(analysis?.cancelCount ?? 0), sub: '累计取消订单', tone: 'warning' as const },
      { label: '退款订单', value: String(analysis?.refundCount ?? 0), sub: '累计退款订单', tone: 'error' as const },
      { label: '趋势天数', value: String(analysis?.dailyOrders.length ?? 0), sub: '最近订单趋势', tone: 'brand' as const },
    ]
  })

  const dataProductStats = computed(() => {
    const analysis = dataProductAnalysis.value
    const totalSalesAmount = (analysis?.items ?? []).reduce((total, item) => total + item.salesAmount, 0)
    return [
      { label: '动销商品', value: String(analysis?.movingProductCount ?? 0), sub: '已产生销售', tone: 'success' as const },
      { label: '销售冠军', value: analysis?.bestSeller || '--', sub: `${analysis?.bestSellerSales ?? 0} 件`, tone: 'brand' as const },
      { label: '滞销商品', value: String(analysis?.slowMovingCount ?? 0), sub: '当前未动销', tone: 'warning' as const },
      { label: '销售额合计', value: options.formatAmount(totalSalesAmount), sub: '当前分析结果', tone: 'brand' as const },
    ]
  })

  const storeRankStats = computed(() => {
    const list = storeRankRows.value
    const totalRevenue = list.reduce((total, item) => total + item.revenue, 0)
    const totalOrders = list.reduce((total, item) => total + item.orderCount, 0)
    return [
      { label: '参与排行门店', value: String(list.length), sub: '实时查询', tone: 'brand' as const },
      { label: '营业额合计', value: options.formatAmount(totalRevenue), sub: '全部排行门店', tone: 'success' as const },
      { label: '订单合计', value: String(totalOrders), sub: '已支付订单', tone: 'warning' as const },
      { label: '第一名', value: list[0]?.storeName || '--', sub: list[0] ? options.formatAmount(list[0].revenue) : '暂无数据', tone: 'brand' as const },
    ]
  })

  async function loadDataOverview(): Promise<void> {
    if (!options.isDataOverviewCenter.value) return
    loadingDataOverview.value = true
    try {
      dataOverviewData.value = await fetchDataOverview()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '经营概览加载失败')
    } finally {
      loadingDataOverview.value = false
    }
  }

  async function loadDataOrders(): Promise<void> {
    if (!options.isDataOrdersCenter.value) return
    loadingDataOrders.value = true
    try {
      dataOrderAnalysis.value = await fetchDataOrderAnalysis()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '订单分析加载失败')
    } finally {
      loadingDataOrders.value = false
    }
  }

  async function loadDataProducts(): Promise<void> {
    if (!options.isDataProductsCenter.value) return
    loadingDataProducts.value = true
    try {
      dataProductAnalysis.value = await fetchDataProductAnalysis()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '商品分析加载失败')
    } finally {
      loadingDataProducts.value = false
    }
  }

  async function loadStoreRank(): Promise<void> {
    if (!options.isStoreRankCenter.value) return
    loadingStoreRank.value = true
    try {
      storeRankRows.value = await fetchStoreRank()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '门店排行加载失败')
    } finally {
      loadingStoreRank.value = false
    }
  }

  return {
    loadingDataOverview,
    loadingDataOrders,
    loadingDataProducts,
    loadingStoreRank,
    dataOverviewData,
    dataOrderAnalysis,
    dataProductAnalysis,
    storeRankRows,
    dataOverviewStats,
    dataOrderStats,
    dataProductStats,
    storeRankStats,
    loadDataOverview,
    loadDataOrders,
    loadDataProducts,
    loadStoreRank,
  }
}
